import { until } from "@vueuse/core";

import { computed, watch } from "vue";

import { useWorktreeManager } from "@/composables/worktree-manager";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { type ProductInTree } from "@/stores/products-store";

import { ProductEntity } from ".";
import { BaseLinkedEntitiesManager } from "../mystekz-linked-entities/manager";

/**
 * Manages the business entities and replaces
 */
export class ProductsManager extends BaseLinkedEntitiesManager<ProductEntity> {
    nodeType = "product" as const;
    worktreeManager = useWorktreeManager();
    currentEntityManager = useCurrentEntityManager();

    constructor(baseUrl: string) {
        super(baseUrl);
        const isLoading = computed(() => {
            return (
                this.worktreeManager.isFetching.value ||
                this.organizationManager.isLoadingOrganizations.value ||
                this.stekManager.isLoadingSteks.value
            );
        });
        this.ready = until(isLoading).not.toBeTruthy();

        watch(
            [
                this.worktreeManager.worktree,
                () => this.currentEntityManager.domain.value?.identity,
            ],
            ([newWorktree, currentDomainId]) => {
                if (!newWorktree || !currentDomainId) {
                    this.setEntities([]);
                    return;
                }

                const products: Array<ProductInTree & { group: string }> = [];
                for (const domain of newWorktree.domains) {
                    if (domain.identity !== currentDomainId) {
                        continue;
                    }

                    for (const product of domain.products) {
                        products.push({
                            ...product,
                            group: domain.name,
                        });
                    }
                }
                this.setEntities(products);
            },
            { immediate: true }
        );
    }

    itemToEntity(product: ProductInTree & { group: string }): ProductEntity {
        const orgName = this.organizationManager.organization.value?.name;
        const stekName = this.stekManager.stek.value?.name;
        const linkedProcesses = product.processes.map((process) => ({
            name: process.name,
            id: process.identity,
            stekName,
            orgName,
            type: "process" as const,
            href: encodeURI(
                (
                    this.baseUrl +
                    "/" +
                    orgName +
                    "/steks/" +
                    stekName +
                    "/process/" +
                    process.identity
                ).replace(/([^:])\/{2,}/g, "$1/")
            ),
            state: "ready" as const,
            data: process,
        }));

        const entity = {
            name: product.name,
            id: product.identity,
            group: product.group,
            order_index: product.order_index,
            stekName,
            orgName,
            type: this.nodeType,
            data: {
                ...product,
                linkedProcesses,
            },
            state: "ready",
        } as ProductEntity;
        entity.href = this.getHref(entity);
        return entity;
    }
}
